import { useMemo, useState } from 'react';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import auctionService from '@/services/auction.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import './styles/CollectorShippingForm.css';

export default function CollectorShippingForm({ orderData, onSuccess })
{
  const { cacheHandler, stateHandler } = useStateHandler();
  const [formState, setFormState] = useState(() => Utils.initShippingAddressForm(orderData?.order_shipping_address));
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState(null);
  const [serverError, setServerError] = useState(null);

  const updateFormState = (key, value) => {
    setFormState((prev) => Utils.addAttributeToObject(key, value, prev));
  };

  const isSubmitDisabled = useMemo(() => {
    return !formState.address_name ||
      !formState.address_surname ||
      !formState.address_street ||
      !formState.address_city ||
      !formState.address_region ||
      !formState.address_zip_code ||
      !formState.address_country ||
      !formState.address_phone?.address_phone_number;
  }, [formState]);

  async function handleSubmit(event)
  {
    event.preventDefault();
    if (!orderData?._id)
    {
      return;
    }

    setLoading(true);
    setFieldError(null);
    setServerError(null);

    try
    {
      const { response, data } = await auctionService.submitOrderShippingAddress(orderData._id, formState);
      if (response.ok)
      {
        setLoading(false);
        setFormState(Utils.initShippingAddressForm(data.order?.order_shipping_address));
        if (typeof onSuccess === 'function')
        {
          onSuccess(data.order, data.shipping_quote ?? null);
        }
        cacheHandler.triggerAction("PROVIDE_SHIPPING_NUMBER");
        stateHandler.set("temporalPopup", {
          text: "Shipping address saved! Review the order to complete payment.",
        });
      }
      else if (response.status === 400)
      {
        setLoading(false);
        setFieldError({
          element: data?.error_data?.element ?? 'address_name',
          error_code: data?.error_type ?? 'SHIPPING_ERROR',
        });
      }
      else
      {
        setLoading(false);
        setServerError("We couldn't save your shipping address. Please try again.");
      }
    }
    catch (error)
    {
      console.error("[CollectorShippingForm] Failed to submit shipping address", error);
      setLoading(false);
      setServerError("We couldn't save your shipping address. Please try again.");
    }
  }

  return (
    <form className="collector-shipping-form" onSubmit={handleSubmit}>
      <Text className="collector-shipping-form__title">
        Add your shipping address
      </Text>
      <Text className="collector-shipping-form__subtitle">
        We'll use it to calculate the shipping cost and prepare your order.
      </Text>
      <div className="collector-shipping-form__group">
        <PublicFormInput
          value={formState.address_name}
          onChange={(e) => updateFormState("address_name", e.target.value)}
          readOnly={loading}
          error={fieldError}
          placeholder="Name"
          element="address_name"
          margin
        />
        <PublicFormInput
          value={formState.address_surname}
          onChange={(e) => updateFormState("address_surname", e.target.value)}
          readOnly={loading}
          error={fieldError}
          placeholder="Surname"
          element="address_surname"
          margin
        />
      </div>
      <PublicFormInput
        value={formState.address_street}
        onChange={(e) => updateFormState("address_street", e.target.value)}
        readOnly={loading}
        error={fieldError}
        placeholder="Address"
        element="address_street"
        margin
      />
      <div className="collector-shipping-form__group">
        <PublicFormInput
          value={formState.address_city}
          onChange={(e) => updateFormState("address_city", e.target.value)}
          readOnly={loading}
          error={fieldError}
          placeholder="City"
          element="address_city"
          margin
        />
        <PublicFormInput
          value={formState.address_zip_code}
          onChange={(e) => updateFormState("address_zip_code", e.target.value)}
          readOnly={loading}
          error={fieldError}
          placeholder="Zip code"
          element="address_zip_code"
          margin
        />
      </div>
      <div className="collector-shipping-form__group">
        <PublicFormInput
          value={formState.address_region}
          onChange={(e) => updateFormState("address_region", e.target.value)}
          readOnly={loading}
          error={fieldError}
          placeholder="Province/Region"
          element="address_region"
          margin
        />
        <PublicFormInput
          value={formState.address_country}
          onChange={(value) => updateFormState("address_country", value)}
          readOnly={loading}
          error={fieldError}
          placeholder="Country"
          element="address_country"
          margin
          type="select"
          selectOptions={{ options: config.forms.prefix_options, type: "country" }}
        />
      </div>
      <div className="collector-shipping-form__group collector-shipping-form__group--phone">
        <PublicFormInput
          value={formState.address_phone?.address_phone_prefix ?? ""}
          onChange={(value) => updateFormState("address_phone.address_phone_prefix", value)}
          readOnly={loading}
          error={fieldError}
          placeholder="Phone prefix"
          element="address_phone.address_phone_prefix"
          margin
          type="select"
          selectOptions={{ options: config.forms.prefix_options, type: "phone_prefix" }}
        />
        <PublicFormInput
          value={formState.address_phone?.address_phone_number ?? ""}
          onChange={(e) => updateFormState("address_phone.address_phone_number", e.target.value)}
          readOnly={loading}
          error={fieldError}
          placeholder="Phone number"
          element="address_phone.address_phone_number"
          margin
        />
      </div>
      {serverError && (
        <span className="collector-shipping-form__error">
          {serverError}
        </span>
      )}
      <ContinueButton
        className="collector-shipping-form__button"
        loading={loading}
        turnOff={isSubmitDisabled}
      >
        Continue
      </ContinueButton>
    </form>
  );
}
